import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useConfig } from '../../../../contexts/config-context';
import { useTranslation } from '../../../../contexts/translation-context';
import { useId } from '../../../../hooks/use-id';
import { CoreDropdownComponent } from '../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdown/CoreDropdown';
import { OptionFactory } from '../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdownOption';
import { toLongCountryCode } from '../phone-input-utils';
import { CountryOption } from './country-option';
import { classes, st } from './dropdown-with-search.st.css';
import { FlagButton } from './flag-button';
import { Search } from './search';
export const DropdownWithSearch = _ref => {
  var _countries$;
  let {
    className,
    theme,
    countryCode,
    countries,
    onSelect,
    searchPhrase,
    onSearch,
    onKeyDown,
    readOnly,
    disabled,
    ariaLabel
  } = _ref;
  const {
    isRTL: rtl
  } = useConfig();
  const longCountryCode = toLongCountryCode(countryCode);
  const flagButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const {
    t
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchStyle, setSearchStyle] = useState({});
  const {
    comboboxProps,
    listboxProps,
    setActiveDescendant
  } = useComboxbox();
  const options = useMemo(() => countries.map(createOption), [countries]);
  const initialSelectedIds = countryCode ? [countryCode] : [];
  const firstCountryCode = (_countries$ = countries[0]) == null ? void 0 : _countries$.shortCountryCode;
  const focusOnOption = searchPhrase ? firstCountryCode : countryCode ?? firstCountryCode;
  const handleKeydown = useCallback(event => {
    var _dropdownRef$current, _flagButtonRef$curren, _dropdownRef$current2;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        (_dropdownRef$current = dropdownRef.current) == null || _dropdownRef$current.close();
        (_flagButtonRef$curren = flagButtonRef.current) == null || _flagButtonRef$curren.focus();
        break;
      case 'ArrowUp':
      case 'ArrowDown':
      case 'Enter':
      case 'Tab':
        (_dropdownRef$current2 = dropdownRef.current) == null || _dropdownRef$current2.onKeyDown(event);
        break;
    }
    onKeyDown(event);
  }, [onKeyDown]);
  const handleSelect = useCallback(option => {
    const _countryCode = option == null ? void 0 : option.id;
    onSelect(_countryCode);
    setIsOpen(false);
  }, [onSelect]);
  const handleSearch = useCallback(event => onSearch(event.target.value), [onSearch]);
  const handleExpandChange = useCallback(open => {
    setIsOpen(open);
    onSearch('');
  }, [onSearch]);
  const handleSearchMounted = useCallback(input => {
    var _input$getBoundingCli;
    const width = input == null || (_input$getBoundingCli = input.getBoundingClientRect()) == null ? void 0 : _input$getBoundingCli.width;
    width && setSearchStyle({
      minWidth: width
    });
  }, []);
  const handleOptionHover = useCallback(option => {
    option && setActiveDescendant(option._DOMid);
  }, [setActiveDescendant]);
  return /*#__PURE__*/React.createElement(CoreDropdownComponent, {
    appendTo: "window",
    readOnly: readOnly,
    disabled: disabled,
    contentId: listboxProps.id,
    contentRole: listboxProps.role,
    className: st(classes.dropdown, {
      rtl
    }),
    contentClassName: st(classes.dropdownContainer, {
      rtl,
      theme
    }),
    ref: dropdownRef,
    initialSelectedIds: initialSelectedIds,
    options: options,
    placement: rtl ? 'bottom-end' : 'bottom-start',
    openTrigger: "click",
    onSelect: handleSelect,
    onExpandedChange: handleExpandChange,
    onOptionHover: handleOptionHover,
    focusOnOption: focusOnOption,
    eventsEnabled: false,
    noResultsText: t('field.phone.country-selector-dropdown.no-result'),
    fixedHeader: /*#__PURE__*/React.createElement("div", {
      style: searchStyle,
      ref: handleSearchMounted,
      className: classes.searchContainer
    }, /*#__PURE__*/React.createElement(Search, _extends({}, comboboxProps, {
      className: className,
      theme: theme,
      autoFocus: true,
      value: searchPhrase,
      onChange: handleSearch,
      onKeyDown: handleKeydown
    })))
  }, /*#__PURE__*/React.createElement(FlagButton, {
    readOnly: readOnly,
    ref: flagButtonRef,
    arrowDirection: isOpen ? 'up' : 'down',
    longCountryCode: longCountryCode,
    ariaLabel: ariaLabel
  }));
};
function createOption(_ref2) {
  let {
    longCountryCode,
    shortCountryCode,
    callingCode,
    countryName
  } = _ref2;
  return OptionFactory.create({
    id: shortCountryCode,
    render: () => /*#__PURE__*/React.createElement(CountryOption, {
      callingCode: callingCode,
      longCountryCode: longCountryCode,
      countryName: countryName
    })
  });
}
function useComboxbox() {
  const listboxId = useId();
  const [activeDescendant, setActiveDescendant] = useState();
  const comboboxProps = {
    role: 'combobox',
    'aria-autocomplete': 'list',
    'aria-haspopup': 'listbox',
    'aria-expanded': true,
    'aria-owns': listboxId,
    'aria-activedescendant': activeDescendant
  };
  const listboxProps = {
    role: 'listbox',
    id: listboxId
  };
  return {
    comboboxProps,
    listboxProps,
    setActiveDescendant
  };
}
//# sourceMappingURL=dropdown-with-search.js.map